import {IProductSnippet} from 'shared/types/productSnippet.types';
import {useProductAvailability} from './useProductAvailability';
import {useProductPrice} from './useProductPrice';
import {useProductRatings} from './useProductRatings';
import {useProductFineAndRare} from './useProductFineAndRare';
import {useProductLabels} from './useProductLabels';
import {useProductIsArt} from './useProductIsArt';
import {IProductShort} from 'modules/products/types';
import {useWishlist} from 'modules/product-snippet/components/ProductSnippetContainer/hooks/useWishlist';
import {useCart} from 'modules/product-snippet/components/ProductSnippetContainer/hooks/useCart';
import {IAnalyticsMeta, TSnippetVersion} from 'core/types';
import {useProductProperties} from 'modules/product-snippet/components/ProductSnippetContainer/hooks/useProductProperties';
import {useAnalytics} from 'modules/product-snippet/components/ProductSnippetContainer/hooks/useAnalytics';
import {useProductStickers} from 'modules/product-snippet/components/ProductSnippetContainer/hooks/useProductStickers';

type TUseProductSnippetData = {
    product: IProductShort;
    isPrive: boolean;
    isWorker: boolean;
    isLoggedIn: boolean;
    analyticsData?: IAnalyticsMeta;
    snippetVersion?: TSnippetVersion;
};

export const useProductSnippetParams = (
    params: TUseProductSnippetData,
): Omit<IProductSnippet, 'hasCartActions' | 'hasBorder'> => {
    const {product, isPrive, isWorker, isLoggedIn, analyticsData, snippetVersion = 'v2'} = params;
    const availability = useProductAvailability(product, isPrive);
    const price = useProductPrice({product, availability, snippetVersion});
    const {ratings, reviewsUrl} = useProductRatings(product);
    const isFineAndRare = useProductFineAndRare(product.properties);
    // const labels = useProductLabels(product.labels, product.price.isOnlineEcom);
    const labels = useProductLabels(product.labels, snippetVersion);
    const isArt = useProductIsArt(product.properties);
    const stickers = useProductStickers({sections: product.sections, properties: product.properties});

    const analytics = useAnalytics({product, analyticsData, price, isFineAndRare});

    const properties = useProductProperties({
        properties: product.properties,
        isListType: false,
        sections: product.sections,
        onCatalogClick: analytics.onCatalogClick,
    });

    const wishlist = useWishlist({
        productData: {
            id: product.id,
            price: product.price,
            sections: product.sections,
            properties: product.properties,
            name: product.name,
        },
        analyticsData,
        onRemoveFromWishlistClick: analytics.onRemoveFromWishlistClick,
        onAddToWishlistClick: analytics.onAddToWishlistClick,
    });

    const cart = useCart({
        productData: product,
        isLoggedIn,
        isWorker,
        isPrive,
        availability,
        onAddToCartClick: analytics.onAddToCartClick,
        onRemoveFromCartClick: analytics.onRemoveFromCartClick,
    });

    return {
        id: product.id,
        title: product.title || '',
        description: product.description,
        url: product.url,
        images: [product.media.imagePreview, ...product.media.images],
        labels,
        isArt,
        price,
        unitsPerPack: product.qtyPerUOM,
        ratings,
        reviewsCount: product.reviewsCount,
        reviewsUrl,
        stickers,
        sections: product.sections,
        availability,
        isFineAndRare,
        cart,
        wishlist,
        properties,
        analytics,
    };
};
